/**
 * Landing → web-app hand-off contract.
 *
 * The landing site (www.itsbaba.com) sends visitors here mid-translation via
 * `https://app.itsbaba.com/translate?text=...&from=...&to=...&gender=...&
 * listener=...&src=...`. This module is the single, pure place that parses
 * and validates that querystring — every field is whitelisted/limited so a
 * malformed or hostile URL degrades to "no hand-off", never to bad state.
 */

export type HandoffFrom = 'en' | 'he' | 'auto'
export type HandoffTo = 'en' | 'he'
export type HandoffSpeakerGender = 'm' | 'f'
export type HandoffListener = 'm' | 'f' | 'group'

export interface ParsedHandoff {
  /** Trimmed source text, or null if absent/empty/over the length cap. */
  text: string | null
  from: HandoffFrom | null
  to: HandoffTo | null
  /** Speaker's own gender ("I am"). */
  gender: HandoffSpeakerGender | null
  /** Who they're speaking to. */
  listener: HandoffListener | null
  /** Free-form surface tag (e.g. 'landing_hero', 'exit_intent') for attribution. */
  src: string | null
}

/** Hand-off carries at most this many characters of source text. */
export const HANDOFF_MAX_TEXT_CHARS = 1000

/** Every querystring key this contract reads — used to strip the URL after parsing. */
export const HANDOFF_PARAM_KEYS = ['text', 'from', 'to', 'gender', 'listener', 'src'] as const

const FROM_VALUES = new Set<HandoffFrom>(['en', 'he', 'auto'])
const TO_VALUES = new Set<HandoffTo>(['en', 'he'])
const GENDER_VALUES = new Set<HandoffSpeakerGender>(['m', 'f'])
const LISTENER_VALUES = new Set<HandoffListener>(['m', 'f', 'group'])
// Surface tags are internal identifiers, not free text — keep them boring.
const SRC_RE = /^[a-zA-Z0-9_-]{1,64}$/

function whitelisted<T extends string>(value: string | null, allowed: Set<T>): T | null {
  return value !== null && allowed.has(value as T) ? (value as T) : null
}

/**
 * Parse and validate a hand-off querystring (e.g. `location.search`).
 * Returns null when none of the recognized params are present at all, so
 * callers can cheaply skip hand-off handling on every other page load.
 */
export function parseHandoff(search: string): ParsedHandoff | null {
  const params = new URLSearchParams(search)
  if (!HANDOFF_PARAM_KEYS.some((key) => params.has(key))) return null

  const rawText = params.get('text')
  const trimmedText = rawText?.trim() ?? ''
  const text = trimmedText && trimmedText.length <= HANDOFF_MAX_TEXT_CHARS ? trimmedText : null

  const from = whitelisted(params.get('from'), FROM_VALUES)
  const to = whitelisted(params.get('to'), TO_VALUES)
  const gender = whitelisted(params.get('gender'), GENDER_VALUES)
  const listener = whitelisted(params.get('listener'), LISTENER_VALUES)

  const rawSrc = params.get('src')
  const src = rawSrc && SRC_RE.test(rawSrc) ? rawSrc : null

  if (!text && !from && !to && !gender && !listener && !src) return null

  return { text, from, to, gender, listener, src }
}
